import { EUserType, useUserStore } from '@/store';

type ChatMsg = Record<string, unknown>;

function pickId(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (s === 'undefined' || s === 'null') return '';
  return s;
}

export function getRolePrefixFromType(roleType: unknown): 'bz' | 'tgz' | '' {
  if (roleType == EUserType.BZ || roleType === '1' || roleType === 1) {
    return 'bz';
  }
  if (roleType == EUserType.TGZ || roleType === '2' || roleType === 2) {
    return 'tgz';
  }
  return '';
}

export function parseRolePrefixFromConversationKey(
  conversationId: string,
): 'bz' | 'tgz' | '' {
  if (conversationId.startsWith('bz_')) return 'bz';
  if (conversationId.startsWith('tgz_')) return 'tgz';
  return '';
}

export function stripRolePrefixFromConversationKey(
  conversationId: string,
): string {
  if (conversationId.startsWith('tgz_')) {
    return conversationId.slice(4);
  }
  if (conversationId.startsWith('bz_')) {
    return conversationId.slice(3);
  }
  return conversationId;
}

/** 构建会话 bucket key：{bz|tgz}_{id}_{userId} */
export function buildConversationKey(
  id: unknown,
  userId: unknown,
  roleType?: unknown,
  scope: 'reg' | 'group' = 'reg',
): string {
  const entityId = pickId(id);
  if (!entityId || userId == null || userId === '') return '';

  const rolePrefix = getRolePrefixFromType(
    roleType ?? useUserStore().user.type,
  );

  if (!rolePrefix) return `${scope}_${entityId}_${userId}`;
  return `${rolePrefix}_${scope}_${entityId}_${userId}`;
}

/** 会话 key 是否属于当前用户与角色（后缀 userId + 前缀 bz/tgz） */
export function isCurrentUserConversationKey(
  conversationId: string,
  userId: unknown,
  roleType?: unknown,
): boolean {
  if (!conversationId || userId == null || userId === '') return false;
  const body = stripRolePrefixFromConversationKey(conversationId);
  const i = body.lastIndexOf('_');
  const suffix = i >= 0 ? body.slice(i + 1) : '';
  if (suffix != userId) return false;

  const expectedRole = getRolePrefixFromType(roleType);
  if (!expectedRole) return true;

  const actualRole = parseRolePrefixFromConversationKey(conversationId);
  if (!actualRole) return true;
  return actualRole === expectedRole;
}

export function pickMessageField(
  msg: ChatMsg,
  camel: string,
  snake: string,
): string {
  return pickId(msg[camel]) || pickId(msg[snake]);
}

// /** 从 conversationId 解析 registrationId */
// export function parseRegistrationIdFromConversationKey(
//   conversationId: string,
//   userId: unknown,
//   roleType?: unknown,
// ): string {
//   if (!conversationId || userId == null || userId === '') return '';
//   if (
//     roleType != null &&
//     roleType !== '' &&
//     roleType !== 0 &&
//     !isCurrentUserConversationKey(conversationId, userId, roleType)
//   ) {
//     return '';
//   }
//   const body = stripRolePrefixFromConversationKey(conversationId);
//   const suffix = '_' + String(userId);
//   if (!body.endsWith(suffix)) return '';
//   return pickId(body.slice(0, body.length - suffix.length));
// }

/** 是否为错误的单聊 bucket（如 bz_undefined_userId） */
export function isBrokenRegistrationConversationKey(
  conversationId: string,
  userId: unknown,
  roleType?: unknown,
): boolean {
  if (!conversationId || userId == null || userId === '') return true;
  if (
    roleType != null &&
    roleType !== '' &&
    roleType !== 0 &&
    !isCurrentUserConversationKey(conversationId, userId, roleType)
  ) {
    return false;
  }
  const body = stripRolePrefixFromConversationKey(conversationId);
  const suffix = '_' + String(userId);
  if (!body.endsWith(suffix)) return false;
  const rawEntity = body.slice(0, body.length - suffix.length);
  if (rawEntity.startsWith('group_')) return false;
  const regId = rawEntity.startsWith('reg_')
    ? rawEntity.slice(4)
    : rawEntity;
  return !pickId(regId);
}

/** 从消息字段、bucket key 或已有会话中补全 registrationId */
/** 规范化 WS / 本地消息字段，补全 registrationId、announcementId */
export function normalizeIncomingChatMessage(
  message: ChatMsg,
): ChatMsg {
  const registrationId = pickMessageField(
    message,
    'registrationId',
    'registration_id',
  );
  const announcementId = pickMessageField(
    message,
    'announcementId',
    'announcement_id',
  );

  const normalized: ChatMsg = { ...message };
  if (registrationId) normalized.registrationId = registrationId;
  if (announcementId) normalized.announcementId = announcementId;
  return normalized;
}

export const getConversationId = (message: ChatMsg) => {
  const userStore = useUserStore();
  const userId = userStore.user.userId;
  const roleType = userStore.user.type;
  const m = normalizeIncomingChatMessage(message);

  if (m.chatType === 'person-message' || m.chatType === 'flow') {
    const regId = pickId(m.registrationId);
    if (!regId) return '';
    return buildConversationKey(regId, userId, roleType, 'reg');
  }
  if (m.chatType === 'groupNotify') {
    const annId = pickId(m.announcementId);
    if (!annId) return '';
    return buildConversationKey(annId, userId, roleType, 'group');
  }
  return '';
};

/** 修复本地 storage 中错误的 bucket，合并到正确 registrationId 分组 */
export function repairChatHistoryBuckets(
  data: Record<string, ChatMsg[]>,
  userId: unknown,
  roleType?: unknown,
): Record<string, ChatMsg[]> {
  if (!data || userId == null || userId === '') return data ?? {};

  const resolvedRole = roleType ?? useUserStore().user.type;
  const result: Record<string, ChatMsg[]> = {};

  const append = (key: string, msg: ChatMsg) => {
    if (!key && !msg) return;
    const normalized = normalizeIncomingChatMessage(msg);
    const targetKey =
      normalized.chatType === 'groupNotify'
        ? buildConversationKey(
            pickId(normalized.announcementId),
            userId,
            resolvedRole,
            'group',
          )
        : pickId(normalized.registrationId)
          ? buildConversationKey(
              pickId(normalized.registrationId),
              userId,
              resolvedRole,
              'reg',
            )
          : key;

    if (
      (normalized.chatType === 'person-message' ||
        normalized.chatType === 'flow') &&
      isBrokenRegistrationConversationKey(
        String(targetKey),
        userId,
        resolvedRole,
      )
    ) {
      return;
    }

    const item: ChatMsg = { ...normalized, conversationId: targetKey };
    if (!result[targetKey]) result[targetKey] = [];
    const exists = result[targetKey].some(
      (m) => m.sessionId === item.sessionId,
    );
    if (!exists) result[targetKey].push(item);
  };

  for (const [key, msgs] of Object.entries(data)) {
    const brokenForCurrent = isBrokenRegistrationConversationKey(
      key,
      userId,
      resolvedRole,
    );
    const belongsToCurrent = isCurrentUserConversationKey(
      key,
      userId,
      resolvedRole,
    );
    const hasUserSuffix = key.lastIndexOf('_') >= 0;
    const belongsToOther =
      hasUserSuffix && !belongsToCurrent && !brokenForCurrent;

    if (belongsToOther) continue;

    for (const msg of msgs) {
      if (brokenForCurrent && msg.chatType !== 'groupNotify') {
        append('', { ...msg, conversationId: key });
      } else if (belongsToCurrent || !hasUserSuffix) {
        append(key, { ...msg, conversationId: key });
      }
    }
  }

  return result;
}
